import React, { useState, useCallback, useRef, useEffect } from 'react';
import { reportsAPI, etapasAPI, tiposProjetoAPI, instituicoesAPI, partnersAPI, usersAPI, empresaAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
  FileText, Filter, Download, Search, Clock, AlertTriangle, CheckCircle, 
  Building, FolderOpen, DollarSign, Calendar, BarChart3, PieChart, Users
} from 'lucide-react';
import ReportFavorites from '../components/ReportFavorites';
import LaudosMonthSection from '../components/LaudosMonthSection';
import PagamentosMonthSection from '../components/PagamentosMonthSection';
import { gerarRelatorioProjetosPDF } from './pdf/RelatorioProjetosPDF';
import { useAuth } from '../context/AuthContext';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

const formatCPF = (cpf) => {
  if (!cpf) return '-';
  return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('pt-BR');
};

const Reports = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(true);
  const [activeTab, setActiveTab] = useState('projetos');
  const [detalhado, setDetalhado] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [empresaConfig, setEmpresaConfig] = useState(null);
  
  // Dados para filtros
  const [etapas, setEtapas] = useState([]);
  const [tiposProjeto, setTiposProjeto] = useState([]);
  const [instituicoes, setInstituicoes] = useState([]);
  const [parceiros, setParceiros] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  
  // Resultados
  const [report, setReport] = useState(null);
  
  // Filtros
  const [filters, setFilters] = useState({
    mes: '',
    ano: new Date().getFullYear().toString(),
    etapa_id: '',
    tipo_projeto_id: '',
    instituicao_id: '',
    parceiro_id: '',
    usuario_id: '',
    status: '',
    pendencia: '',
    valor_min: '',
    valor_max: '',
    data_inicio: '',
    data_fim: '',
    data_arquivamento_inicio: '',
    data_arquivamento_fim: '',
  });
  
  const tableRef = useRef(null);

  // Carregar dados para os filtros
  useEffect(() => {
    const loadFilterData = async () => {
      try {
        setLoadingFilters(true);
        const [etapasRes, tiposRes, instRes, parcRes, usersRes, empresaRes] = await Promise.all([
          etapasAPI.list(),
          tiposProjetoAPI.list(),
          instituicoesAPI.list(),
          partnersAPI.list(),
          usersAPI.list().catch(() => ({ data: [] })),
          empresaAPI.getConfig().catch(() => ({ data: null })),
        ]);
        
        setEmpresaConfig(empresaRes.data);
        setEtapas(etapasRes.data || []);
        setTiposProjeto(tiposRes.data || []);
        setInstituicoes(instRes.data || []);
        setParceiros(parcRes.data || []);
        setUsuarios(usersRes.data || []);
      } catch (error) {
        console.error('Erro ao carregar filtros:', error);
      } finally {
        setLoadingFilters(false);
      }
    };
    
    loadFilterData();
  }, []);

  const handleSearch = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      
      if (filters.mes && filters.mes !== 'all') params.mes = parseInt(filters.mes);
      if (filters.ano && filters.ano !== 'all') params.ano = parseInt(filters.ano);
      if (filters.etapa_id && filters.etapa_id !== 'all') params.etapa_id = filters.etapa_id;
      if (filters.tipo_projeto_id && filters.tipo_projeto_id !== 'all') params.tipo_projeto_id = filters.tipo_projeto_id;
      if (filters.instituicao_id && filters.instituicao_id !== 'all') params.instituicao_id = filters.instituicao_id;
      if (filters.parceiro_id && filters.parceiro_id !== 'all') params.parceiro_id = filters.parceiro_id;
      if (filters.usuario_id && filters.usuario_id !== 'all') params.usuario_id = filters.usuario_id;
      if (filters.status && filters.status !== 'all') params.status = filters.status;
      if (filters.pendencia && filters.pendencia !== 'all') params.pendencia = filters.pendencia === 'true';
      if (filters.valor_min) params.valor_min = parseFloat(filters.valor_min);
      if (filters.valor_max) params.valor_max = parseFloat(filters.valor_max);
      if (filters.data_inicio) params.data_inicio = filters.data_inicio;
      if (filters.data_fim) params.data_fim = filters.data_fim;
      if (filters.data_arquivamento_inicio) params.data_arquivamento_inicio = filters.data_arquivamento_inicio;
      if (filters.data_arquivamento_fim) params.data_arquivamento_fim = filters.data_arquivamento_fim;

      const response = await reportsAPI.summary(params);
      // Ordenar projetos por nome do cliente em ordem alfabética
      if (response.data && response.data.projetos) {
        response.data.projetos.sort((a, b) => 
          (a.cliente_nome || '').localeCompare(b.cliente_nome || '', 'pt-BR')
        );
      }
      setReport(response.data);
    } catch (error) {
      toast.error('Erro ao gerar relatório');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const handleClearFilters = () => {
    setFilters({
      mes: '',
      ano: new Date().getFullYear().toString(),
      etapa_id: '',
      tipo_projeto_id: '',
      instituicao_id: '',
      parceiro_id: '',
      usuario_id: '',
      status: '',
      pendencia: '',
      valor_min: '',
      valor_max: '',
      data_inicio: '',
      data_fim: '',
      data_arquivamento_inicio: '',
      data_arquivamento_fim: '',
    });
    setReport(null);
  };

  const statusLabels = { em_andamento: 'Em Andamento', arquivado: 'Arquivado', desistido: 'Desistido' };

  const handleExportPDF = async () => {
    if (!report) {
      toast.error('Gere um relatório primeiro');
      return;
    }
    try {
      setExporting(true);
      const filtrosLabels = [];
      if (filters.mes && filters.mes !== 'all') filtrosLabels.push({ label: 'Mês', value: months.find(m => m.value === filters.mes)?.label || filters.mes });
      if (filters.ano && filters.ano !== 'all') filtrosLabels.push({ label: 'Ano', value: filters.ano });
      if (filters.etapa_id && filters.etapa_id !== 'all') filtrosLabels.push({ label: 'Etapa', value: etapas.find(e => e.id === filters.etapa_id)?.nome || '-' });
      if (filters.tipo_projeto_id && filters.tipo_projeto_id !== 'all') filtrosLabels.push({ label: 'Tipo de Projeto', value: tiposProjeto.find(t => t.id === filters.tipo_projeto_id)?.nome || '-' });
      if (filters.instituicao_id && filters.instituicao_id !== 'all') filtrosLabels.push({ label: 'Instituição', value: instituicoes.find(i => i.id === filters.instituicao_id)?.nome || '-' });
      if (filters.parceiro_id && filters.parceiro_id !== 'all') filtrosLabels.push({ label: 'Parceiro', value: parceiros.find(p => p.id === filters.parceiro_id)?.nome || '-' });
      if (filters.usuario_id && filters.usuario_id !== 'all') filtrosLabels.push({ label: 'Usuário', value: usuarios.find(u => u.id === filters.usuario_id)?.nome || '-' });
      if (filters.status && filters.status !== 'all') filtrosLabels.push({ label: 'Status', value: statusLabels[filters.status] || filters.status });
      if (filters.pendencia && filters.pendencia !== 'all') filtrosLabels.push({ label: 'Pendência', value: filters.pendencia === 'true' ? 'Com pendência' : 'Sem pendência' });
      if (filters.valor_min || filters.valor_max) filtrosLabels.push({ label: 'Valor', value: `${filters.valor_min ? formatCurrency(parseFloat(filters.valor_min)) : '—'} até ${filters.valor_max ? formatCurrency(parseFloat(filters.valor_max)) : '—'}` });
      if (filters.data_inicio || filters.data_fim) filtrosLabels.push({ label: 'Data Início', value: `${filters.data_inicio ? formatDate(filters.data_inicio) : '—'} até ${filters.data_fim ? formatDate(filters.data_fim) : '—'}` });
      if (filters.data_arquivamento_inicio || filters.data_arquivamento_fim) filtrosLabels.push({ label: 'Arquivamento', value: `${filters.data_arquivamento_inicio ? formatDate(filters.data_arquivamento_inicio) : '—'} até ${filters.data_arquivamento_fim ? formatDate(filters.data_arquivamento_fim) : '—'}` });
      if (filtrosLabels.length === 0) filtrosLabels.push({ label: 'Filtros', value: 'Nenhum (todos os projetos)' });

      await gerarRelatorioProjetosPDF({
        report,
        detalhado,
        empresa: empresaConfig,
        filtrosLabels,
        geradoPor: user?.nome || '—',
      });
      toast.success('PDF gerado com sucesso');
    } catch (e) {
      console.error(e);
      toast.error('Falha ao gerar PDF: ' + (e.message || 'erro desconhecido'));
    } finally {
      setExporting(false);
    }
  };

  const handleExportCSV = () => {
    if (!report) {
      toast.error('Gere um relatório primeiro');
      return;
    }

    const headers = ['Cliente', 'CPF', 'Tipo Projeto', 'Instituição', 'Valor Crédito', 'Etapa', 'Status', 'Duração (dias)', 'Parceiro', 'Usuário', 'Pendência', 'Data Início'];
    const rows = report.projetos.map(p => [
      p.cliente_nome,
      p.cliente_cpf,
      p.tipo_projeto || '',
      p.instituicao || '',
      p.valor_credito,
      p.etapa_atual,
      p.status,
      p.duracao_total_dias,
      p.parceiro || '',
      p.usuario || '',
      p.tem_pendencia ? 'Sim' : 'Não',
      p.data_inicio || ''
    ]);

    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio_agrolink_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
    toast.success('CSV exportado com sucesso');
  };

  const months = [
    { value: '1', label: 'Janeiro' },
    { value: '2', label: 'Fevereiro' },
    { value: '3', label: 'Março' },
    { value: '4', label: 'Abril' },
    { value: '5', label: 'Maio' },
    { value: '6', label: 'Junho' },
    { value: '7', label: 'Julho' },
    { value: '8', label: 'Agosto' },
    { value: '9', label: 'Setembro' },
    { value: '10', label: 'Outubro' },
    { value: '11', label: 'Novembro' },
    { value: '12', label: 'Dezembro' },
  ];

  const years = Array.from({ length: 10 }, (_, i) => (new Date().getFullYear() - i).toString());

  if (loadingFilters) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <BarChart3 className="w-7 h-7" />
            Relatórios
          </h1>
          <p className="text-muted-foreground">Gere relatórios detalhados com filtros avançados</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2" data-testid="toggle-detalhado-container">
            <Switch
              id="pdf-detalhado"
              checked={detalhado}
              onCheckedChange={setDetalhado}
              data-testid="toggle-pdf-detalhado"
            />
            <Label htmlFor="pdf-detalhado" className="text-sm cursor-pointer whitespace-nowrap">
              Detalhado
            </Label>
          </div>
          <Button variant="outline" onClick={handleExportCSV} disabled={!report} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" />
            CSV
          </Button>
          <Button onClick={handleExportPDF} disabled={!report || exporting} data-testid="export-pdf-btn">
            <Download className="w-4 h-4 mr-2" />
            {exporting ? 'Gerando...' : 'PDF'}
          </Button>
        </div>
      </div>

      {/* Laudos do Mês (Fase 3) */}
      <LaudosMonthSection />

      {/* Pagamentos do Mês (Fase 4) */}
      <PagamentosMonthSection />

      {/* Filters Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtros Avançados
          </CardTitle>
          <CardDescription>
            Configure os filtros para gerar relatórios personalizados
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Linha 1: Período */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Período
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Mês</Label>
                <Select value={filters.mes} onValueChange={(v) => setFilters({ ...filters, mes: v })}>
                  <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {months.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Ano</Label>
                <Select value={filters.ano} onValueChange={(v) => setFilters({ ...filters, ano: v })}>
                  <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {years.map((y) => (
                      <SelectItem key={y} value={y}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Data Início</Label>
                <Input
                  type="date"
                  value={filters.data_inicio}
                  onChange={(e) => setFilters({ ...filters, data_inicio: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Data Fim</Label>
                <Input
                  type="date"
                  value={filters.data_fim}
                  onChange={(e) => setFilters({ ...filters, data_fim: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Arquivado de</Label>
                <Input
                  type="date"
                  value={filters.data_arquivamento_inicio}
                  onChange={(e) => setFilters({ ...filters, data_arquivamento_inicio: e.target.value })}
                  data-testid="filter-data-arquivamento-inicio"
                />
              </div>
              <div className="space-y-2">
                <Label>Arquivado até</Label>
                <Input
                  type="date"
                  value={filters.data_arquivamento_fim}
                  onChange={(e) => setFilters({ ...filters, data_arquivamento_fim: e.target.value })}
                  data-testid="filter-data-arquivamento-fim"
                />
              </div>
            </div>
          </div>

          {/* Linha 2: Projeto */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
              <FolderOpen className="w-4 h-4" />
              Projeto
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Tipo de Projeto</Label>
                <Select value={filters.tipo_projeto_id} onValueChange={(v) => setFilters({ ...filters, tipo_projeto_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {tiposProjeto.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Etapa Atual</Label>
                <Select value={filters.etapa_id} onValueChange={(v) => setFilters({ ...filters, etapa_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Todas" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {etapas.map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                  <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="em_andamento">Em Andamento</SelectItem>
                    <SelectItem value="arquivado">Arquivado</SelectItem>
                    <SelectItem value="desistido">Desistido</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Pendência</Label>
                <Select value={filters.pendencia} onValueChange={(v) => setFilters({ ...filters, pendencia: v })}>
                  <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="true">Com Pendência</SelectItem>
                    <SelectItem value="false">Sem Pendência</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Linha 3: Instituição e Parceiro */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
              <Building className="w-4 h-4" />
              Instituição, Parceiro e Usuário
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Instituição Financeira</Label>
                <Select value={filters.instituicao_id} onValueChange={(v) => setFilters({ ...filters, instituicao_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Todas" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {instituicoes.map((i) => (
                      <SelectItem key={i.id} value={i.id}>{i.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Parceiro/Indicação</Label>
                <Select value={filters.parceiro_id} onValueChange={(v) => setFilters({ ...filters, parceiro_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {parceiros.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Usuário Responsável</Label>
                <Select value={filters.usuario_id} onValueChange={(v) => setFilters({ ...filters, usuario_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {usuarios.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Linha 4: Valores */}
          <div>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Valores
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Valor Mínimo (R$)</Label>
                <Input
                  type="number"
                  placeholder="0,00"
                  value={filters.valor_min}
                  onChange={(e) => setFilters({ ...filters, valor_min: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Valor Máximo (R$)</Label>
                <Input
                  type="number"
                  placeholder="999.999,00"
                  value={filters.valor_max}
                  onChange={(e) => setFilters({ ...filters, valor_max: e.target.value })}
                />
              </div>
            </div>
          </div>

          {/* Botões */}
          <div className="flex justify-between items-center gap-3 pt-4 border-t flex-wrap">
            <ReportFavorites
              modulo="projetos"
              filtrosAtuais={filters}
              onAplicarFavorito={(f) => setFilters(prev => ({ ...prev, ...f }))}
            />
            <div className="flex gap-3">
              <Button variant="outline" onClick={handleClearFilters}>
                Limpar Filtros
              </Button>
              <Button onClick={handleSearch} disabled={loading}>
                <Search className="w-4 h-4 mr-2" />
                {loading ? 'Gerando...' : 'Gerar Relatório'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {report && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <FolderOpen className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Total Projetos</p>
                    <p className="text-xl font-bold">{report.resumo.total_projetos}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Valor Total</p>
                    <p className="text-lg font-bold">{formatCurrency(report.resumo.total_credito)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Com Pendência</p>
                    <p className="text-xl font-bold text-amber-600">{report.resumo.com_pendencia}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Em Andamento</p>
                    <p className="text-xl font-bold">{report.resumo.por_status.em_andamento}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Arquivados</p>
                    <p className="text-xl font-bold">{report.resumo.por_status.arquivado}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <PieChart className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Ticket Médio</p>
                    <p className="text-lg font-bold">
                      {formatCurrency(report.resumo.total_projetos > 0 ? report.resumo.total_credito / report.resumo.total_projetos : 0)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card data-testid="card-clientes-novos">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                    <Users className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Clientes Cadastrados</p>
                    <p className="text-xl font-bold text-indigo-600">{report.resumo.clientes_novos_no_periodo ?? 0}</p>
                    <p className="text-[10px] text-muted-foreground">no período filtrado</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Distribution Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Por Etapa */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Distribuição por Etapa</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(report.resumo.por_etapa).map(([etapa, count]) => (
                    <div key={etapa} className="flex items-center justify-between">
                      <span className="text-sm">{etapa}</span>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Por Tipo Projeto */}
            {report.resumo.por_tipo_projeto && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Distribuição por Tipo</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Object.entries(report.resumo.por_tipo_projeto).map(([tipo, count]) => (
                      <div key={tipo} className="flex items-center justify-between">
                        <span className="text-sm">{tipo}</span>
                        <Badge variant="secondary">{count}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Clientes cadastrados no período */}
          {(report.resumo.clientes_novos_lista || []).length > 0 && (
            <Card data-testid="card-clientes-novos-lista">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Users className="w-4 h-4 text-indigo-600" />
                  Clientes cadastrados no período ({report.resumo.clientes_novos_no_periodo})
                </CardTitle>
                <CardDescription className="text-xs">
                  Novos clientes criados dentro da janela de datas do filtro (por data de cadastro).
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="rounded-lg border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead>Cliente</TableHead>
                        <TableHead className="hidden sm:table-cell">CPF</TableHead>
                        <TableHead className="hidden md:table-cell">Telefone</TableHead>
                        <TableHead className="hidden lg:table-cell">Parceiro</TableHead>
                        <TableHead>Data cadastro</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.resumo.clientes_novos_lista.map((c) => (
                        <TableRow key={c.id} data-testid={`cli-novo-${c.id}`}>
                          <TableCell className="font-medium">{c.nome_completo || '-'}</TableCell>
                          <TableCell className="hidden sm:table-cell text-xs text-muted-foreground">{c.cpf || '-'}</TableCell>
                          <TableCell className="hidden md:table-cell text-xs text-muted-foreground">{c.telefone || '-'}</TableCell>
                          <TableCell className="hidden lg:table-cell text-xs">{c.parceiro || '-'}</TableCell>
                          <TableCell className="text-xs">
                            {c.created_at ? new Date(c.created_at).toLocaleDateString('pt-BR') : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Detalhamento ({report.projetos.length} projetos)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div ref={tableRef} className="rounded-lg border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>Cliente</TableHead>
                      <TableHead className="hidden sm:table-cell">CPF</TableHead>
                      <TableHead className="hidden md:table-cell">Tipo</TableHead>
                      <TableHead className="hidden lg:table-cell">Instituição</TableHead>
                      <TableHead>Valor</TableHead>
                      <TableHead className="hidden md:table-cell">Etapa</TableHead>
                      <TableHead className="hidden lg:table-cell">Status</TableHead>
                      <TableHead className="hidden lg:table-cell">Duração</TableHead>
                      <TableHead className="hidden xl:table-cell">Parceiro</TableHead>
                      <TableHead className="hidden xl:table-cell">Usuário</TableHead>
                      <TableHead>Pendência</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.projetos.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                          Nenhum projeto encontrado
                        </TableCell>
                      </TableRow>
                    ) : (
                      report.projetos.map((proj) => (
                        <TableRow key={proj.id}>
                          <TableCell className="font-medium">{proj.cliente_nome}</TableCell>
                          <TableCell className="hidden sm:table-cell mono text-sm">
                            {formatCPF(proj.cliente_cpf)}
                          </TableCell>
                          <TableCell className="hidden md:table-cell">
                            <Badge variant="outline">{proj.tipo_projeto || '-'}</Badge>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell text-sm">
                            {proj.instituicao || '-'}
                          </TableCell>
                          <TableCell>{formatCurrency(proj.valor_credito)}</TableCell>
                          <TableCell className="hidden md:table-cell">
                            <Badge variant="secondary">{proj.etapa_atual}</Badge>
                          </TableCell>
                          <TableCell className="hidden lg:table-cell capitalize">{proj.status}</TableCell>
                          <TableCell className="hidden lg:table-cell">
                            <div className="flex items-center gap-1 text-sm">
                              <Clock className="w-3 h-3" />
                              {proj.duracao_total_dias}d
                            </div>
                          </TableCell>
                          <TableCell className="hidden xl:table-cell text-sm">{proj.parceiro || '-'}</TableCell>
                          <TableCell className="hidden xl:table-cell text-sm">{proj.usuario || '-'}</TableCell>
                          <TableCell>
                            {proj.tem_pendencia ? (
                              <Badge variant="destructive" className="gap-1">
                                <AlertTriangle className="w-3 h-3" />
                                Sim
                              </Badge>
                            ) : (
                              <Badge className="gap-1 bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
                                <CheckCircle className="w-3 h-3" />
                                Não
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Loading State */}
      {loading && (
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-64" />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Reports;
